import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import {
  Card, 
  CardContent, 
  CardHeader, 
} from '@/components/ui/card';
import { 
  ChevronRight, 
  ChevronDown, 
  FolderOpen,
  Folder,
  Search,
  Wallet,
  Landmark,
  TrendingUp,
  TrendingDown,
  Scale,
  Circle
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { PageHeader } from '@/components/page-header';
import type { AccountNode } from '@/types';

export const Route = createFileRoute('/coa')({
  component: CoAStatementPage,
});

function AccountRow({ node, level = 0 }: { node: AccountNode; level?: number }) {
  const [isExpanded, setIsExpanded] = useState(true);
  const hasChildren = node.children && node.children.length > 0;

  const getTypeBadgeColor = (type: string) => {
    switch (type) {
      case 'ASSET': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'LIABILITY': return 'bg-red-100 text-red-800 border-red-200';
      case 'EQUITY': return 'bg-green-100 text-green-800 border-green-200';
      case 'REVENUE': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'EXPENSE': return 'bg-orange-100 text-orange-800 border-orange-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Get icon based on account type and whether it's a header
  const getAccountIcon = () => {
    // Debug: log account info
    console.log('[COA Icon]', node.code, node.name, 'type:', node.type, 'isHeader:', node.isHeader, 'hasChildren:', hasChildren);
    
    // Header accounts (parent with children) use folder icons
    if (node.isHeader || hasChildren) {
      return isExpanded && hasChildren 
        ? <FolderOpen className="h-[18px] w-[18px] text-primary" />
        : <Folder className="h-[18px] w-[18px] text-primary/80" />;
    }
    
    // Leaf accounts use type-specific icons (case-insensitive match)
    const type = (node.type || '').toUpperCase();
    switch (type) {
      case 'ASSET':
        return <Wallet className="h-4 w-4 text-blue-500" />;
      case 'LIABILITY':
        return <Landmark className="h-4 w-4 text-red-500" />;
      case 'EQUITY':
        return <Scale className="h-4 w-4 text-green-500" />;
      case 'REVENUE':
        return <TrendingUp className="h-4 w-4 text-purple-500" />;
      case 'EXPENSE':
        return <TrendingDown className="h-4 w-4 text-orange-500" />;
      default:
        console.log('[COA Icon] Unknown type:', node.type, 'for', node.code);
        return <Circle className="h-4 w-4 text-muted-foreground" />;
    }
  };

  return (
    <div className="select-none">
      <div 
        className={`flex items-center py-2 px-3 hover:bg-muted/50 rounded-md transition-colors cursor-pointer group ${node.isHeader ? 'font-semibold bg-muted/20' : ''}`}
        style={{ paddingLeft: `${level * 24 + 12}px` }}
        onClick={() => hasChildren && setIsExpanded(!isExpanded)}
      >
        {/* Expand/Chevron indicator */}
        <span className="w-6 flex items-center justify-center mr-1">
          {hasChildren ? (
            isExpanded 
              ? <ChevronDown className="h-4 w-4 text-muted-foreground/70" /> 
              : <ChevronRight className="h-4 w-4 text-muted-foreground/70" />
          ) : (
            <span className="w-4" /> // Spacer for alignment
          )}
        </span>
        
        {/* Account Icon */}
        <span className="mr-3 flex items-center justify-center w-5">
          {getAccountIcon()}
        </span>
        
        {/* Account Code */}
        <span className="w-20 font-mono text-sm text-muted-foreground mr-4">
          {node.code}
        </span>
        
        {/* Account Name */}
        <span className={`flex-1 truncate ${node.isHeader ? 'text-foreground' : 'text-foreground/90'}`}>
          {node.name}
        </span>

        {/* Badges - always visible on mobile, hover on desktop */}
        <div className="flex items-center gap-2 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
          <Badge variant="outline" className={`text-[10px] px-1.5 py-0 h-5 lowercase ${getTypeBadgeColor(node.type)}`}>
            {node.type.toLowerCase()}
          </Badge>
          {node.normalBalance && (
            <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-5 hidden sm:inline-flex">
              {node.normalBalance === 'DEBIT' ? 'Dr' : 'Cr'}
            </Badge>
          )}
        </div>
      </div>
      
      {/* Children */}
      {hasChildren && isExpanded && (
        <div className="border-l-2 border-muted ml-[23px] mt-1 space-y-0.5">
          {node.children!.map((child) => (
            <AccountRow key={child.id} node={child} level={level + 1} />
          ))}
        </div>
      )}
    </div>
  );
}

function CoAStatementPage() {
  const [searchTerm, setSearchTerm] = useState('');
  
  const { data: response, isLoading, error } = useQuery({
    queryKey: ['accounts', 'tree'],
    queryFn: () => api.accounts.get('tree'),
  });

  const accounts = response?.data || [];

  const filterAccounts = (nodes: AccountNode[], term: string): AccountNode[] => {
    if (!term) return nodes;
    
    return nodes.reduce((acc: AccountNode[], node) => {
      const match = node.name.toLowerCase().includes(term.toLowerCase()) || 
                    node.code.includes(term);
      
      const filteredChildren = node.children ? filterAccounts(node.children, term) : [];
      
      if (match || filteredChildren.length > 0) {
        acc.push({
          ...node,
          children: filteredChildren
        });
      }
      
      return acc;
    }, []);
  };

  const filteredData = filterAccounts(accounts, searchTerm);

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 pb-10">
      <PageHeader
        title="Chart of Accounts"
        description="Manage and view your accounting structure"
      />

      <Card>
        <CardHeader className="pb-3 px-6">
          <div className="flex items-center justify-between gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or code..."
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-3 pb-6">
          {isLoading ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : error ? (
            <div className="text-center py-20 text-destructive">
              <p>Failed to load accounts. Please try again.</p>
            </div>
          ) : filteredData.length === 0 ? (
            <div className="text-center py-20 text-muted-foreground">
              <p>No accounts found matching your search.</p>
            </div>
          ) : (
            <div className="space-y-1">
              {filteredData.map((node) => (
                <AccountRow key={node.id} node={node} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
